// Blocages / kill-switch (SA-4) — porté de la maquette Super Admin v2 (bloc `isKill`) : cartes des blocages
// ACTIFS (niveau, granularité, cible, motif, auteur, conséquence, Lever), panneau « Règles automatiques »
// (la règle LOCATION est branchée et s'évalue à chaque lecture ; l'échéance de paiement attend le signal
// Sliderz Business — dit, jamais simulé), et l'HISTORIQUE complet (période, motif, auteur, levé par).
// Garde-fous SA-4.5 à la POSE : cible NOMMÉE, conséquence ÉNONCÉE selon niveau+granularité, motif d'au
// moins 5 caractères. ⚠️ Le motif est désormais AMORÇABLE par un motif type (renversement assumé du
// 2026-08-13) — le seuil et la confirmation portent sur le texte FINAL, pré-rempli ou non.
// DEUX EFFETS, DEUX MOMENTS, et l'écran doit les distinguer : le REFUS D'APPAIRAGE est immédiat (la
// Console le vérifie elle-même à la redemption du QR) ; l'arrêt de l'encaissement d'une caisse EN SERVICE
// suppose que la consigne lui soit PARVENUE (canal « blockade » → garde §3.0bis au démarrage suivant ou au
// prochain cycle de sync). D'où `propagated` : tant qu'il est faux, les caisses encaissent — et l'écran le
// dit, au lieu d'afficher « bloqué » sur une caisse qui travaille (§23.0).
import { dateTimeLong } from '@orgazone/date-format';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type BlockRow, type TenantRow, type StoreRow, type DeviceRow } from '../api';
import { deviceRef } from '../device-name';
import { AUTO_BLOCK_RULES } from '../block-rules';
import { useAuth, isSuperadmin } from '../auth';
import { showToast, useOverlayPanel } from '@orgazone/ui-web';
import { EmptyState } from '@orgazone/ui-web';

import { Pill } from '../ui';
import { TableScroll } from '../table-scroll';
const fmtDT = dateTimeLong; // « 4 août 2026 à 14:32 » — rendu partagé (@orgazone/date-format)
const LEVEL = {
  warn: { label: 'avertissement', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  lock: { label: 'blocage', bg: 'var(--da-bg)', fg: 'var(--da-fg)' },
} as const;
const SCOPE_LABEL = { subscription: 'abonnement', store: 'magasin', device: 'caisse' } as const;

/** « le blocage » / « l’avertissement » — l'élision se calcule, elle ne se recopie pas à chaque phrase. */
const withArticle = (mot: string): string => (/^[aeiouyéèêâîôû]/i.test(mot) ? `l’${mot}` : `le ${mot}`);
/** Une phrase porte une majuscule, même quand elle commence par une variable (les niveaux sont en bas de casse). */
const capitalize = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * Motifs types — le texte qu'ils posent est celui que l'utilisateur LIRA EN CAISSE.
 *
 * ⚠️ RENVERSEMENT ASSUMÉ de SA-4.5 (décision utilisateur 2026-08-13). La règle d'origine exigeait un
 * motif entièrement écrit, « jamais pré-rempli », au motif qu'un texte suggéré serait accepté sans
 * réflexion. L'arbitrage inverse a été rendu : le texte type devient un POINT DE DÉPART, toujours
 * modifiable. Ce qui protégeait la décision reste en place — la confirmation nominative et le seuil
 * de 5 caractères s'appliquent au texte FINAL, pré-rempli ou non.
 *
 * ⚠️ Le texte s'adresse au CLIENT en caisse, pas à l'exploitant : il dit quoi faire pour sortir du
 * blocage. Un motif interne (« relance n° 3 ») n'a rien à faire ici.
 */
/**
 * LES MODÈLES DE CONSIGNE VIENNENT DU RÉFÉRENTIEL ÉDITEUR (`referential`, liste `modeles_consigne`),
 * plus du code (demande utilisateur 2026-09-05).
 *
 * ⚠️ POURQUOI ILS ONT DÛ EN SORTIR : ce sont les textes que l'exploitant lit sur SA caisse quand tout
 * s'arrête. Ils se retouchent au fil de l'usage et s'enrichissent au cas par cas — les tenir en dur
 * imposait une livraison de la Console pour changer une virgule. Ils s'éditent désormais dans
 * Référentiels › Modèles de consigne, RETOURS À LA LIGNE COMPRIS.
 *
 * ⚠️ AUCUN REPLI EN DUR ICI, ET C'EST VOULU (§23.0) : si la liste est vide ou illisible, on ne
 * propose AUCUN modèle et on le DIT — la saisie libre reste entière. Un jeu de textes de secours
 * recréerait exactement la double vérité qu'on vient de supprimer.
 *
 * ⚠️ ET ILS SONT FILTRÉS PAR NIVEAU (question utilisateur 2026-09-06). La liste servait les deux
 * indifféremment : « votre abonnement a expiré » pouvait partir en AVERTISSEMENT, et l'inverse. Ce
 * ne sont pas deux tons du même message — un avertissement doit porter une échéance et un remède, un
 * blocage doit porter un contact. L'erreur était silencieuse : elle ne se voyait que sur la caisse
 * d'un commerçant. ⚠️ Le filtre reste une COURTOISIE : le texte demeure libre après sélection, donc
 * la garantie vit dans les règles automatiques (qui choisissent par niveau), pas dans ce formulaire.
 */
interface ModeleConsigne { code: string; label: string; message: string; niveau: string }

// Conséquence ÉNONCÉE (SA-4.5) — ce qui va cesser de fonctionner, par niveau × granularité.
// ⚠️ Deux effets DISTINCTS, et il faut le dire : le refus de MISE EN SERVICE est immédiat (la Console le
// vérifie elle-même à la redemption du QR) ; l'arrêt de l'encaissement suppose que la caisse ait REÇU la
// consigne — elle la lit au démarrage suivant, ou au prochain cycle de synchronisation. Annoncer les deux
// au même présent laisserait croire qu'une caisse allumée s'arrête à la seconde (§23.0).
const consequenceOf = (scope: BlockRow['scope'], level: BlockRow['level']): string => {
  if (level === 'warn') return 'Message d’avertissement (écartable) — rien ne s’arrête.';
  const target = scope === 'device' ? 'Cette caisse' : scope === 'store' ? 'Aucune caisse de ce magasin' : 'Aucune caisse du parc de ce client';
  const verb = scope === 'device' ? 'elle refusera' : 'elles refuseront';
  return `${target} ne peut plus être mise en service. Dès réception de la consigne — au démarrage ou au prochain cycle de synchronisation — ${verb} d’encaisser.`;
};

/**
 * L'EFFET D'UNE LEVÉE — le miroir de `consequenceOf`, et pour la même raison.
 *
 * ⚠️ LEVER UN AVERTISSEMENT NE ROUVRE RIEN (retour utilisateur 2026-09-06 : « j'ai levé un
 * avertissement et il me dit que les caisses sont rouvertes »). Rien n'était fermé : le message
 * cessera simplement d'être affiché. Annoncer une réouverture laisse croire qu'une caisse était
 * arrêtée — donc qu'un magasin a perdu des ventes — et fait douter de tout le reste de l'écran.
 */
const liftEffectOf = (scope: BlockRow['scope'], level: BlockRow['level']): string => {
  if (level === 'warn') return 'Effet : le message cesse d’être affiché à la prochaine synchronisation. Rien n’était arrêté, rien ne redémarre.';
  return scope === 'subscription' ? 'Effet : tout le parc du client retrouve la mise en service et l’encaissement à la prochaine synchronisation.'
    : scope === 'store' ? 'Effet : les caisses de ce magasin retrouvent l’ouverture de session à la prochaine synchronisation.'
      : 'Effet : cette caisse retrouve l’ouverture de session à la prochaine synchronisation.';
};

/**
 * Où en est la DESCENTE d'une consigne vers les caisses (une décision n'est pas une application).
 *
 * ⚠️ UN AVERTISSEMENT VOYAGE AUSSI, et l'écran le taisait : cet état n'était rendu que pour un
 * blocage. Or un message qui n'est pas parvenu n'est affiché nulle part — la Console montrait donc un
 * avertissement « actif » que personne ne voyait, sans rien en dire (§23.0). Seuls les MOTS changent :
 * un blocage s'applique, un avertissement s'affiche.
 */
function DeliveryState({ ok, phase, level }: { ok: boolean; phase: 'pose' | 'levée'; level: BlockRow['level'] }): React.ReactElement {
  const verbe = level === 'lock' ? 'appliqué' : 'affiché';
  return ok
    ? <Pill bg="var(--ok-bg)" fg="var(--ok-fg)">{phase === 'pose' ? `${verbe} en caisse` : 'levée appliquée'}</Pill>
    : <Pill bg="var(--wa-bg)" fg="var(--wa-fg)">{phase === 'pose' ? `pas encore ${verbe} en caisse` : 'levée pas encore appliquée'}</Pill>;
}

// ── Modale de POSE (module-level). Cible passée en preset (boutons « Bloquer » des fiches) ou choisie ici. ──
export interface BlockPreset { scope: BlockRow['scope']; targetId: string; targetLabel: string }
function NewBlockModal({ preset, onClose, onDone }: { preset: BlockPreset | null; onClose: () => void; onDone: (msg: string) => void }) {
  /** Le préfixe des identifiants qui relient un libellé à son champ, ou un titre à son groupe. */
  const uid = useId();
  const [scope, setScope] = useState<BlockRow['scope']>(preset?.scope ?? 'device');
  const [targetId, setTargetId] = useState(preset?.targetId ?? '');
  const [level, setLevel] = useState<'warn' | 'lock'>('warn');
  // Le motif type CHOISI — `null` dès que le texte s'en écarte (saisie libre ou « Autre »).
  const [kind, setKind] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant la pose, comme le clic sur le fond : la fermer laisserait croire qu'elle n'a pas eu lieu.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  // Modèles de consigne (référentiel éditeur) — chargés à l'ouverture, jamais figés dans le code.
  const [modeles, setModeles] = useState<ModeleConsigne[] | null>(null);
  const [modelesErr, setModelesErr] = useState<string | null>(null);
  useEffect(() => {
    api.referentials()
      .then((r) => setModeles(r.referentials
        .filter((x) => x.list === 'modeles_consigne')
        .sort((a, b) => a.ord - b.ord)
        .map((x) => ({
          code: x.code, label: x.label,
          message: typeof x.attrs?.message === 'string' ? x.attrs.message : '',
          niveau: typeof x.attrs?.niveau === 'string' ? x.attrs.niveau : '',
        }))))
      .catch((e) => setModelesErr((e as Error).message));
  }, []);
  /**
   * LES MODÈLES PROPOSÉS POUR CE NIVEAU — et eux seuls.
   * ⚠️ Un modèle SANS formulation (« Autre ») sert les deux : il n'ouvre que la saisie libre, il n'y a
   *    donc aucune formulation à contredire.
   * ⚠️ UN MODÈLE À {VARIABLES} EST ÉCARTÉ : ce sont ceux qu'une règle automatique remplit. Posé à la
   *    main, « échéance {echeance} » s'afficherait TEL QUEL sur la caisse — personne ne le remplirait.
   *    On lit la marque dans le texte plutôt que d'entretenir ici une copie de la liste du serveur.
   */
  const offered = useMemo(
    () => (modeles ?? []).filter((m) => !m.message.trim() || (m.niveau === level && !/\{[a-z_]+\}/.test(m.message))),
    [modeles, level],
  );

  /**
   * CHANGER DE NIVEAU RETIRE UN TEXTE QU'ON N'A PAS ÉCRIT, jamais un texte qu'on a écrit.
   * ⚠️ C'est tout l'objet de cette passe : garder « renouvelez avant cette date pour que la caisse
   * continue de fonctionner » sur un BLOCAGE dirait à un exploitant que sa caisse tourne encore,
   * alors qu'elle est arrêtée. Mais effacer en silence ce que quelqu'un a rédigé serait hostile —
   * d'où la distinction : on ne retire que ce qui vient d'un modèle devenu inadapté.
   */
  useEffect(() => {
    if (kind && !offered.some((m) => m.code === kind)) { setKind(null); setReason(''); }
  }, [kind, offered]);

  // Cibles réelles pour le sélecteur (hors preset).
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [stores, setStores] = useState<StoreRow[]>([]);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  useEffect(() => {
    if (preset) return;
    // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : une lecture des cibles lancée avant un changement de présélection ne doit
    //    ni remplacer les listes courantes ni poser « Cibles indisponibles » sur un formulaire qui n'a pas échoué.
    let alive = true;
    Promise.all([api.tenants(), api.stores(), api.devices()])
      .then(([t, s, d]) => { if (!alive) return; setTenants(t.tenants); setStores(s.stores); setDevices(d.devices.filter((x) => x.status === 'assigned')); })
      .catch((e) => { if (alive) setErr(`Cibles indisponibles : ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [preset]);

  const targetLabel = preset?.targetLabel
    ?? (scope === 'subscription' ? tenants.find((t) => t.id === targetId)?.name
      : scope === 'store' ? stores.find((s) => s.id === targetId)?.name
      : (() => { const d = devices.find((x) => x.id === targetId); return d ? `${deviceRef(d)}${d.registerNumber != null ? ` · n° ${d.registerNumber}` : ''}` : undefined; })());
  // CONFIRMATION NOMINATIVE (maquette SA-4.5) : couper un client se fait en écrivant son nom. Le geste
  // devient délibéré — on ne bloque pas la mauvaise cible parce qu'un bouton rouge était sous la souris.
  // Exigée pour le BLOCAGE seul : un avertissement n'arrête rien, la friction serait gratuite.
  const [confirmName, setConfirmName] = useState('');
  const needsConfirm = level === 'lock' && !!targetLabel;
  const confirmOk = !needsConfirm || confirmName.trim().toLowerCase() === targetLabel!.trim().toLowerCase();
  const ready = !!targetId && reason.trim().length >= 5 && confirmOk && !busy;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      // ⚠️ Le verdict de DESCENTE fait partie du résultat : un blocage enregistré mais non parvenu aux
      // caisses n'arrête rien, et l'annoncer comme un succès serait le mensonge le plus coûteux ici.
      const r = await api.createBlock({ scope, targetId, level, reason: reason.trim() });
      const target = targetLabel ?? 'la cible';
      onDone(r.propagated
        ? `${capitalize(LEVEL[level].label)} posé sur ${target} — consigne transmise aux caisses.`
        // ⚠️ « Elles continuent d'encaisser » ne dit rien d'un avertissement : elles encaisseraient de
        //    toute façon. Ce qui manque, dans son cas, c'est le message — et c'est ça qu'il faut dire.
        : `${capitalize(LEVEL[level].label)} posé sur ${target}, mais NON transmis aux caisses (${r.failures.map((f) => `${f.gln} : ${f.error}`).join(' · ') || 'back-office injoignable'}). ${level === 'lock' ? 'Elles continuent d’encaisser' : 'Le message ne s’y affiche pas encore'} ; nouvelle tentative automatique.`);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
        style={{ width: 'min(94vw, 480px)' }} onClick={(e) => e.stopPropagation()}>
        {/* ⚠️ LE TITRE ET SA JUSTIFICATION SUIVENT LE NIVEAU CHOISI. « Nouveau blocage … une coupure
            s'assume » au-dessus d'un formulaire réglé sur AVERTISSEMENT annonce une coupure qui n'aura
            pas lieu — et le niveau se change dans ce même formulaire, deux champs plus bas. */}
        <h3 id={`${uid}-title`} style={{ marginBottom: 4 }}>{level === 'lock' ? 'Nouveau blocage' : 'Nouvel avertissement'}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12 }}>
          {level === 'lock'
            ? 'La pose est journalisée (compte, horodatage, IP) et la cible est nommée — une coupure s’assume.'
            : 'La pose est journalisée (compte, horodatage, IP). Rien ne s’arrête : la caisse affichera ce message, l’exploitant pourra l’écarter.'}
        </div>

        {!preset && <>
          <span id={`${uid}-scope`} className="field-title" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>Granularité</span>
          <div role="group" aria-labelledby={`${uid}-scope`} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {(['subscription', 'store', 'device'] as const).map((s) => (
              <button key={s} onClick={() => { setScope(s); setTargetId(''); }}
                className={scope === s ? 'field-opt selected' : 'field-opt'} style={{ flex: 1, justifyContent: 'center' }}>
                {s === 'subscription' ? 'Abonnement' : s === 'store' ? 'Magasin' : 'Caisse'}
              </button>
            ))}
          </div>
          <label htmlFor={`${uid}-target`} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>Cible</label>
          <select id={`${uid}-target`} value={targetId} onChange={(e) => setTargetId(e.target.value)}
            style={{ width: '100%', height: 36, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', marginBottom: 12 }}>
            <option value="">— choisir —</option>
            {scope === 'subscription' && tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            {scope === 'store' && stores.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.tenantName ?? '—'})</option>)}
            {scope === 'device' && devices.map((d) => <option key={d.id} value={d.id}>{deviceRef(d)}{d.registerNumber != null ? ` · n° ${d.registerNumber}` : ''} — {d.storeName ?? '—'}</option>)}
          </select>
        </>}
        {preset && (
          <div style={{ padding: '10px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--ink100)', marginBottom: 12 }}>
            <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>Cible ({SCOPE_LABEL[scope]})</div>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginTop: 2 }}>{preset.targetLabel}</div>
          </div>
        )}

        <span id={`${uid}-level`} className="field-title" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>Niveau</span>
        <div role="group" aria-labelledby={`${uid}-level`} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {(['warn', 'lock'] as const).map((l) => (
            <button aria-pressed={level === l} key={l} onClick={() => setLevel(l)}
              style={{ flex: 1, height: 32, borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 'var(--t-dense)', fontWeight: 700, border: `1px solid ${level === l ? (l === 'lock' ? 'rgba(251,113,133,.5)' : 'var(--wa-fg)') : 'var(--ink200)'}`, background: level === l ? LEVEL[l].bg : 'transparent', color: level === l ? LEVEL[l].fg : 'var(--ink600)' }}>
              {l === 'warn' ? 'Avertissement (écartable)' : 'Blocage (l’usage s’arrête)'}
            </button>
          ))}
        </div>
        {/* Conséquence ÉNONCÉE (SA-4.5) — ce qui va réellement cesser. */}
        <div style={{ fontSize: 'var(--t-note)', color: level === 'lock' ? 'var(--da-fg)' : 'var(--ink500)', lineHeight: 1.5, marginBottom: 12 }}>{consequenceOf(scope, level)}</div>

        {/* Motifs types — le choix POSE le texte, il ne le verrouille pas. Ils viennent du référentiel. */}
        <span id={`${uid}-kind`} className="field-title" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>Motif type</span>
        <div role="group" aria-labelledby={`${uid}-kind`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {offered.map((m) => (
            <button key={m.code} type="button" onClick={() => { setKind(m.code); setReason(m.message); }}
              className={kind === m.code ? 'field-opt selected' : 'field-opt'}>{m.label}</button>
          ))}
          {/* §23.0 : l'absence de modèle se DIT — on ne fabrique pas un jeu de secours en douce.
              ⚠️ Et elle dit POUR QUEL NIVEAU elle manque : « aucun modèle » sur une liste qui en
              contient six ferait chercher une panne là où il n'y a qu'un catalogue incomplet. */}
          {modeles && offered.length === 0 && (
            <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
              {modeles.length === 0
                ? 'Aucun modèle — Référentiels › Modèles de consigne. Le message se saisit librement ci-dessous.'
                : `Aucun modèle ${level === 'lock' ? 'de blocage' : 'd’avertissement'} — Référentiels › Modèles de consigne. Le message se saisit librement ci-dessous.`}
            </span>
          )}
          {modelesErr && <span style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)' }}>Modèles indisponibles ({modelesErr}) — le message se saisit librement.</span>}
        </div>

        <label htmlFor={`${uid}-message`} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>
          Message affiché en caisse <span style={{ color: 'var(--ink500)', fontWeight: 500 }}>(modifiable — le texte type n’est qu’un point de départ)</span>
        </label>
        {/* ⚠️ Écrire à la main DÉSOLIDARISE le texte de son motif type : la pastille se désélectionne,
            sinon l'écran affirmerait un motif que le message ne dit plus. */}
        <textarea id={`${uid}-message`} value={reason} onChange={(e) => { setReason(e.target.value); if (kind && e.target.value !== modeles?.find((m) => m.code === kind)?.message) setKind(null); }} rows={3}
          placeholder="Visible par l'utilisateur à l'ouverture — écrivez-le pour lui, pas pour nous."
          style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${reason.trim().length >= 5 ? 'var(--ink200)' : 'var(--wa-fg)'}`, background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', resize: 'vertical', outline: 'none' }} />
        {needsConfirm && (
          <div style={{ marginTop: 12 }}>
            <label htmlFor={`${uid}-confirm`} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>
              Confirmez en écrivant <b style={{ color: 'var(--da-fg)' }}>{targetLabel}</b>
            </label>
            <input id={`${uid}-confirm`} value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={targetLabel}
              style={{ width: '100%', height: 34, padding: '0 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${confirmOk ? 'var(--ok-fg)' : 'var(--ink200)'}`, background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', outline: 'none' }} />
          </div>
        )}
        {err && <div className="err" style={{ marginTop: 10 }}>{err}</div>}
        <div className="modalfoot">
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={onClose} disabled={busy}>Annuler</button>
          <button className={`btn ${level === 'lock' ? 'danger' : 'primary'}`} style={{ width: 'auto', margin: 0, padding: '9px 18px', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }} disabled={!ready} onClick={() => void submit()}>
            {busy ? 'Pose…' : level === 'lock' ? `Bloquer ${targetLabel ?? ''}` : 'Poser l’avertissement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale de LEVÉE (maquette SA-4.3) : le motif de levée est OBLIGATOIRE. Il l'était déjà côté API
// (`liftBlock(id, reason)`) et l'historique affiche une colonne « levée : … » — qui restait vide par
// construction puisque l'UI levait par un simple confirm(). Rouvrir un accès s'assume comme le couper. ──
function LiftBlockModal({ block, onClose, onDone }: { block: BlockRow; onClose: () => void; onDone: (msg: string) => void }) {
  /** Relie le libellé du motif à son champ. */
  const reasonId = useId();
  const titleId = useId();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant la levée, comme le clic sur le fond. Le focus va au motif par ce socle, jamais par
  // `autoFocus` : posé avant lui, il serait retenu comme déclencheur et le focus ne reviendrait nulle part.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  const ready = reason.trim().length >= 5 && !busy;
  const auto = block.source.startsWith('rule:');

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      // ⚠️ Une levée doit descendre aussi vite qu'une pose : une caisse déverrouillée ici mais toujours
      // bloquée sur place, c'est un magasin à l'arrêt sans raison. On DIT si elle est parvenue.
      const r = await api.liftBlock(block.id, reason.trim());
      // ⚠️ CE QUE LA LEVÉE A ROUVERT DÉPEND DU NIVEAU. Un avertissement n'avait rien fermé : annoncer
      //    des caisses « rouvertes » laisserait croire qu'un magasin était à l'arrêt.
      const locked = block.level === 'lock';
      onDone(r.propagated
        ? `${capitalize(LEVEL[block.level].label)} levé — ${block.label}. ${locked ? 'Les caisses sont rouvertes.' : 'Le message ne s’affichera plus.'}`
        : `${capitalize(LEVEL[block.level].label)} levé — ${block.label}, mais la levée N’EST PAS parvenue aux caisses (${r.failures.map((f) => `${f.gln} : ${f.error}`).join(' · ') || 'back-office injoignable'}). ${locked ? 'Elles restent bloquées' : 'Le message continue de s’afficher'} ; nouvelle tentative automatique.`);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={{ width: 'min(94vw, 460px)' }} onClick={(e) => e.stopPropagation()}>
        <h3 id={titleId} style={{ marginBottom: 4 }}>Lever {withArticle(LEVEL[block.level].label)} — {block.label}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12 }}>La levée est journalisée avec votre compte, son horodatage et ce motif.</div>

        {/* Effet RÉEL de la levée, énoncé avant de la valider. */}
        <div style={{ padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--in-bg)', color: 'var(--in-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55, marginBottom: 12 }}>
          {liftEffectOf(block.scope, block.level)}
        </div>
        {auto && (
          <div style={{ padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55, marginBottom: 12 }}>
            {capitalize(LEVEL[block.level].label)} posé par une <b>règle automatique</b> : si la cause persiste (location toujours en retard), il se reposera à la prochaine lecture.
          </div>
        )}

        <label htmlFor={reasonId} style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 6 }}>Motif de la levée <span style={{ color: 'var(--wa-fg)', fontWeight: 500 }}>(obligatoire)</span></label>
        <textarea id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          placeholder="Ce qui a été régularisé — il sera relu tel quel."
          style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--r-sm)', border: `1px solid ${reason.trim().length >= 5 ? 'var(--ink200)' : 'var(--wa-fg)'}`, background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', resize: 'vertical', outline: 'none' }} />
        {err && <div className="err" style={{ marginTop: 10 }}>{err}</div>}
        <div className="modalfoot">
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '9px 16px' }} onClick={onClose} disabled={busy}>Annuler</button>
          <button className="btn btn-cta" style={{ width: 'auto', margin: 0, padding: '9px 18px', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }} disabled={!ready} onClick={() => void submit()}>
            {busy ? 'Levée…' : 'Lever'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Blocks() {
  const { user } = useAuth();
  const loc = useLocation();
  // Délégation par domaine, reflétée à l'UI (le serveur reste le juge) : billing_ops → abonnement,
  // deploy_ops → caisse/magasin, super-admin → tout.
  const canSub = isSuperadmin(user) || !!user?.roles.includes('billing_ops');
  const canStore = isSuperadmin(user) || !!user?.roles.includes('deploy_ops');
  const canAny = canSub || canStore;

  const [data, setData] = useState<{ active: BlockRow[]; history: BlockRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ⚠️ Le preset venant d'une fiche (bouton « Bloquer ») REPASSE par le contrôle de droit : sinon un
  // compte en lecture seule obtenait le formulaire complet, que le serveur refuse ensuite en 403.
  const [modal, setModal] = useState<{ preset: BlockPreset | null } | null>(() => {
    const preset = (loc.state as { preset?: BlockPreset } | null)?.preset;
    if (!preset) return null;
    const allowed = preset.scope === 'subscription' ? canSub : canStore;
    return allowed ? { preset } : null;
  });

  const load = useCallback(() => api.blocks().then((r) => { setData(r); setErr(null); }).catch((e) => setErr((e as Error).message)), []);
  useEffect(() => { void load(); }, [load]);

  const canLift = (b: BlockRow) => (b.scope === 'subscription' ? canSub : canStore);
  // La levée passe par sa MODALE (motif obligatoire) — un confirm() ne peut pas collecter de texte.
  const [lifting, setLifting] = useState<BlockRow | null>(null);
  // ⚠️ Arrivée depuis un bouton « Lever le blocage » d'une fiche (StoreDetail) : on ouvre la modale de
  //    levée sur le bon blocage DÈS qu'il est chargé et si le droit le permet — même patron que le preset
  //    de pose. Une seule fois (le state de navigation est consommé), sinon elle se rouvrirait à chaque
  //    rechargement de la liste.
  const liftId = (loc.state as { liftId?: string } | null)?.liftId;
  const [liftHonored, setLiftHonored] = useState(false);
  useEffect(() => {
    if (!liftId || liftHonored || !data) return;
    const b = data.active.find((x) => x.id === liftId);
    // Contrôle de droit INLINE (même règle que canLift) — le mettre en dépendance de l'effet le ferait
    // recalculer sans raison ; ici on ne le lit qu'une fois, à l'arrivée depuis la fiche.
    if (b && (b.scope === 'subscription' ? canSub : canStore)) setLifting(b);
    setLiftHonored(true);
  }, [liftId, liftHonored, data, canSub, canStore]);

  const rules = AUTO_BLOCK_RULES;

  return (
    <div className="page">
      {modal && <NewBlockModal preset={modal.preset} onClose={() => setModal(null)} onDone={(m) => { setModal(null); showToast(m); void load(); }} />}
      {lifting && <LiftBlockModal block={lifting} onClose={() => setLifting(null)} onDone={(m) => { setLifting(null); showToast(m); void load(); }} />}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Blocages</h1>
          {/* Sans contrainte de mesure : sur les 13 sous-titres de la maquette, seul celui de la reprise
              en porte une. Les caps inventés ici cassaient des lignes qui tenaient (relevé 2026-08-12). */}
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.5 }}>
            Deux niveaux : <b style={{ color: 'var(--ink700)' }}>avertissement</b> (message écartable) et <b style={{ color: 'var(--ink700)' }}>blocage</b> (l'usage s'arrête).
            Trois granularités : caisse, magasin, abonnement client. L'appairage est refusé aussitôt ; une caisse en service arrête d'encaisser quand la consigne lui est parvenue, à sa prochaine synchronisation — chaque carte dit si c'est fait.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {canAny && (
          <button onClick={() => setModal({ preset: null })}
            className="btn danger soft" style={{ height: 34, padding: '0 15px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)', fontWeight: 700, cursor: 'pointer' }}>
            Nouveau blocage
          </button>
        )}
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      {!data && !err && <div className="muted" style={{ padding: 16 }}>Chargement…</div>}

      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)', gap: 14, marginBottom: 14, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>actifs · {data.active.length}</div>
            {data.active.length === 0 && (
              <div className="dcard">
                <EmptyState icon="lock" title="Aucun blocage sur ce périmètre" note="Ni avertissement, ni blocage en cours — rien n'empêche l'encaissement." />
              </div>
            )}
            {data.active.map((b) => (
              <div key={b.id} className="dcard" style={{ padding: '14px 16px', border: `1px solid ${b.level === 'lock' ? 'rgba(251,113,133,.4)' : 'var(--ink200)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                  <Pill bg={LEVEL[b.level].bg} fg={LEVEL[b.level].fg}>{LEVEL[b.level].label}</Pill>
                  <Pill bg="var(--in-bg)" fg="var(--in-fg)">{SCOPE_LABEL[b.scope]}</Pill>
                  {b.source.startsWith('rule:') && <Pill bg="var(--vi-bg)" fg="var(--vi-fg)">règle auto</Pill>}
                  <DeliveryState ok={b.propagated} phase="pose" level={b.level} />
                  <div style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{fmtDT(b.createdAt)}</span>
                </div>
                <div style={{ fontSize: 'var(--t-control)', fontWeight: 700, marginTop: 9 }}>{b.label}</div>
                <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{b.sub}</div>
                <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink600)', marginTop: 6, lineHeight: 1.55 }}>{b.reason}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ink100)' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{b.createdBy}</div>
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2 }}>{consequenceOf(b.scope, b.level)}</div>
                    {!b.propagated && (
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--wa-fg)', marginTop: 4, lineHeight: 1.5 }}>
                        La consigne n’a pas encore atteint le back-office : {b.level === 'lock'
                          ? <>les caisses concernées <b>continuent d’encaisser</b>.</>
                          : <>le message n’est <b>affiché sur aucune caisse</b>.</>}
                        {' '}Nouvelle tentative à chaque consultation de cet écran.
                      </div>
                    )}
                  </div>
                  {canLift(b)
                    ? <button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 12px' }} onClick={() => setLifting(b)}>Lever</button>
                    : <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)' }} title="Délégation par domaine : ce blocage relève d'un autre rôle">levée non déléguée</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="dcard" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 11 }}>Règles automatiques</div>
            {rules.map((r) => (
              <div key={r.name} style={{ padding: '10px 0', borderBottom: '1px solid var(--ink100)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ flex: 1, fontSize: 'var(--t-body)', fontWeight: 600 }}>{r.name}</span>
                  <Pill bg={r.state === 'active' ? 'var(--ok-bg)' : 'var(--ne-bg)'} fg={r.state === 'active' ? 'var(--ok-fg)' : 'var(--ne-fg)'}>{r.state}</Pill>
                </div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 5, lineHeight: 1.5 }}>{r.desc}</div>
              </div>
            ))}
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 11, lineHeight: 1.5 }}>
              Un blocage posé automatiquement se lève ici, par le rôle habilité au domaine (ou le super-admin) — la levée est journalisée. Si la cause persiste, la règle le repose.
            </div>
          </div>
        </div>

        <div className="dcard" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', fontSize: 'var(--t-title)', fontWeight: 700 }}>Historique</div>
          {data.history.length === 0 ? (
            <EmptyState icon="history" title="Aucun blocage passé" note="L'historique conserve chaque blocage levé, avec son motif et son auteur. Il est vide tant qu'aucun n'a été posé sur ce périmètre." />
          ) : (
            <TableScroll minWidth={680}>
              <thead><tr><th>Période</th><th>Cible</th><th>Niveau</th><th>Motif</th><th>Auteur</th><th>Levé par</th></tr></thead>
              <tbody>
                {data.history.map((b) => (
                  <tr key={b.id}>
                    <td className="mono" style={{ fontSize: 'var(--t-meta)', whiteSpace: 'nowrap' }}>{fmtDT(b.createdAt)} → {b.liftedAt ? fmtDT(b.liftedAt) : '—'}</td>
                    <td style={{ fontWeight: 600 }}>{b.label}<div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', fontWeight: 400 }}>{b.sub}</div></td>
                    <td><Pill bg={LEVEL[b.level].bg} fg={LEVEL[b.level].fg}>{LEVEL[b.level].label}</Pill></td>
                    <td style={{ fontSize: 'var(--t-dense)', color: 'var(--ink600)', maxWidth: 260 }}>{b.reason}{b.liftReason ? <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>levée : {b.liftReason}</div> : null}</td>
                    <td style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{b.createdBy}</td>
                    <td style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{b.liftedBy ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        </div>
      </>}
    </div>
  );
}
